// Module 19 (BL-380) Q-046 - "Words your clients see". The company chooses what it calls an event and a crew member in
// Events Settings > General (event_settings.event_word / crew_word, migration 240, the same shape as org_settings.bookings_label).
// The database keeps the one word; this resolver gives every screen the forms it needs: singular, plural, and lower-case for the
// middle of a sentence. Nothing saved = 'Event' / 'Crew', so every screen reads exactly as before.

export const DEFAULT_EVENT_WORD = 'Event';
export const DEFAULT_CREW_WORD = 'Crew';
export const WORD_PATTERN = /^[A-Za-z0-9]([A-Za-z0-9 &'-]{0,22}[A-Za-z0-9])?$/;
export const WORD_MAX = 24;
export const WORD_RULE = 'Use 1 to 24 letters, numbers, spaces, &, - or \', starting and ending with a letter or number.';

// A saved word, or the default when it is blank or not a valid word (a bad value never reaches a screen).
export function cleanWord(value, fallback) {
    const word = String(value ?? '').trim();
    return WORD_PATTERN.test(word) ? word : fallback;
}

// English plural of the LAST word: Party -> Parties, Class -> Classes, Show -> Shows, Day -> Days. "Crew" -> "Crews".
export function pluralWord(word) {
    const text = String(word || '');
    const match = text.match(/^(.*?)([A-Za-z]+)$/);
    if (!match) return text;
    const [, head, last] = match;
    const upper = last === last.toUpperCase() && last.length > 1;   // an all-capitals word (DJ, VIP) keeps capitals
    const s = upper ? 'S' : 's';
    const es = upper ? 'ES' : 'es';
    if (/[^aeiou]y$/i.test(last)) return `${head}${last.slice(0, -1)}${upper ? 'IES' : 'ies'}`;
    if (/(s|x|z|ch|sh)$/i.test(last)) return `${head}${last}${es}`;
    return `${head}${last}${s}`;
}

// The form for the middle of a sentence: "Party" -> "party", "Bar Mitzvah" -> "bar mitzvah"; a word in capitals (DJ, VIP) stays.
export function lowerWord(word) {
    return String(word || '').split(' ').map((part) => (/^[A-Z][a-z0-9'&-]*$/.test(part) ? part.toLowerCase() : part)).join(' ');
}

// Every form of both words, from Events Settings (or from nothing).
export function resolveEventWords(settings) {
    const event = cleanWord(settings?.event_word, DEFAULT_EVENT_WORD);
    const crew = cleanWord(settings?.crew_word, DEFAULT_CREW_WORD);
    const events = pluralWord(event);
    const crews = pluralWord(crew);
    return {
        event,
        events,
        eventLower: lowerWord(event),
        eventsLower: lowerWord(events),
        crew,
        crews,
        crewLower: lowerWord(crew),
        crewsLower: lowerWord(crews),
    };
}

// "1 event" / "3 events" with the company's word.
export function countWord(count, words, kind = 'event') {
    const n = Number(count) || 0;
    const one = kind === 'crew' ? words.crewLower : words.eventLower;
    const many = kind === 'crew' ? words.crewsLower : words.eventsLower;
    return `${n} ${n === 1 ? one : many}`;
}

export const DEFAULT_EVENT_WORDS = Object.freeze(resolveEventWords(null));
